import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrderApi } from '@hero/api-hooks';
import { useOrderDisplayInfo, useOrderDestination } from '@hero/hooks-shared';
import { isInternalShipment, useViewerLeg } from '@hero/web-hooks';
import { ArrowRightIcon, DriverIcon } from '@hero/web-icons';
import type { Id, Order } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { RouteArrowDisplay } from '../../../../app/RouteArrowDisplay/RouteArrowDisplay';
import { RefDisplay } from '../../../../app/Ref/RefDisplay';
import { OrderStatusBadge } from '../../../Order/Status/OrderStatusBadge';
import { openEntityDrawer } from './openEntityDrawer';

export interface OrderCardWidgetProps {
  orderId: Id;
  displaySnapshot?: {
    ref?: string;
    status?: string;
  };
}

/** Compact order card for the agent chat — route, client, ref/status/driver. */
export const OrderCardWidget = ({ orderId, displaySnapshot }: OrderCardWidgetProps) => {
  const { data: order } = useOrderApi(orderId);

  if (order) {
    return (
      <OrderCardWidgetLoaded
        orderId={orderId}
        order={order}
        displaySnapshot={displaySnapshot}
      />
    );
  }

  return <OrderCardWidgetSnapshot orderId={orderId} displaySnapshot={displaySnapshot} />;
};

const OrderCardWidgetLoaded = ({
  orderId,
  order,
  displaySnapshot,
}: {
  orderId: Id;
  order: Order;
  displaySnapshot?: OrderCardWidgetProps['displaySnapshot'];
}) => {
  const { t } = useTranslation(['m-agent', 'm-order']);
  const viewerLeg = useViewerLeg(order);
  const { orderInfo, status } = useOrderDisplayInfo({
    order,
    displayForUser: 'carrier',
    viewerLeg,
  });
  const { firstPickup, lastDropoff } = useOrderDestination(order.destinations);
  const internal = isInternalShipment(order);

  const clientLabel = internal
    ? t('m-order:badges.internal', { defaultValue: 'Internal order' })
    : order.client?.name ?? '—';

  const driverName = order.driver?.name?.trim();

  return (
    <OrderCardShell orderId={orderId}>
      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <div className='flex min-w-0 items-center justify-between gap-3'>
          <RouteArrowDisplay
            origin={firstPickup ?? { city: { name: '—' } }}
            destinations={[lastDropoff ?? { city: { name: '—' } }]}
            size='small'
            className='min-w-0 flex-1 truncate font-semibold text-slate-900'
          />
          <span className='max-w-[42%] shrink-0 truncate text-end text-xs font-medium text-slate-600'>
            {clientLabel}
          </span>
        </div>

        <div className='flex flex-wrap items-center gap-x-2 gap-y-1'>
          <RefDisplay
            refMain={orderInfo.ref?.main ?? displaySnapshot?.ref}
            refShort={orderInfo.ref?.short}
            className='text-xs'
          />
          <OrderStatusBadge status={status.currentStatus as Order['status']['order']} />
          <span className='inline-flex min-w-0 items-center gap-1 text-xs text-slate-600'>
            <DriverIcon className='size-3.5 shrink-0 text-slate-400' strokeWidth={2} />
            <span className='truncate'>
              {driverName || t('widgets.order_card.no_driver', 'No driver')}
            </span>
          </span>
        </div>
      </div>
      <ArrowRightIcon className='size-4 shrink-0 text-slate-400 rtl:rotate-180' />
    </OrderCardShell>
  );
};

const OrderCardWidgetSnapshot = ({
  orderId,
  displaySnapshot,
}: {
  orderId: Id;
  displaySnapshot?: OrderCardWidgetProps['displaySnapshot'];
}) => {
  const { t } = useTranslation('m-agent');

  return (
    <OrderCardShell orderId={orderId}>
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <p className='text-sm font-semibold text-slate-900'>
          {displaySnapshot?.ref ?? t('widgets.order_card.title', 'Order')}
        </p>
        {displaySnapshot?.status ? (
          <p className='text-xs capitalize text-slate-500'>
            {displaySnapshot.status.replace(/_/g, ' ')}
          </p>
        ) : null}
      </div>
      <ArrowRightIcon className='size-4 shrink-0 text-slate-400 rtl:rotate-180' />
    </OrderCardShell>
  );
};

const OrderCardShell = ({
  orderId,
  children,
}: {
  orderId: Id;
  children: ReactNode;
}) => (
  <button
    type='button'
    onClick={() => openEntityDrawer('order', String(orderId))}
    className={cn(
      'group flex w-full items-center gap-3 rounded-2xl border border-slate-200/90',
      'bg-white px-3.5 py-3 text-start shadow-sm transition-all',
      'hover:border-primary-200 hover:bg-primary-50/30 hover:shadow-md',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
    )}
  >
    {children}
  </button>
);
